import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';

export type BillingStatus = 'none' | 'trialing' | 'active' | 'past_due' | 'canceled';

export type BillingInfo = {
  status: BillingStatus;
  plan: 'starter' | 'pro' | null;
  billingCycle: 'monthly' | 'annual' | null;
  trialEndsAt: string | null;       // ISO date string
  currentPeriodEnd: string | null;
  paymentProvider: 'stripe' | 'gocardless' | 'complimentary' | null;
};

type BillingState = {
  loading: boolean;
  billing: BillingInfo;
  refresh: () => void;
};

const defaultBilling: BillingInfo = {
  status: 'none',
  plan: null,
  billingCycle: null,
  trialEndsAt: null,
  currentPeriodEnd: null,
  paymentProvider: null,
};

const BillingContext = createContext<BillingState>({
  loading: true,
  billing: defaultBilling,
  refresh: () => {},
});

export function BillingProvider({ children }: { children: React.ReactNode }) {
  const [loading, setLoading]   = useState(true);
  const [billing, setBilling]   = useState<BillingInfo>(defaultBilling);
  const [tick, setTick]         = useState(0);

  const refresh = useCallback(() => setTick(t => t + 1), []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    fetch('/api/billing/status')
      .then(res => {
        if (!res.ok) return null;
        return res.json();
      })
      .then((data: BillingInfo | null) => {
        if (cancelled) return;
        if (data) setBilling(data);
        else setBilling(defaultBilling);
      })
      .catch(() => {
        if (!cancelled) setBilling(defaultBilling);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [tick]);

  return (
    <BillingContext.Provider value={{ loading, billing, refresh }}>
      {children}
    </BillingContext.Provider>
  );
}

export function useBilling() {
  return useContext(BillingContext);
}

/** Returns the number of days remaining in the trial, or 0 if expired/not trialing. */
export function trialDaysRemaining(trialEndsAt: string | null): number {
  if (!trialEndsAt) return 0;
  const ms = new Date(trialEndsAt).getTime() - Date.now();
  return Math.max(0, Math.ceil(ms / (1000 * 60 * 60 * 24)));
}
